import { lastsWeeksOf } from './subscriptionBuilder'
import type { LastsWeeksSource, Product } from '../types/catalog'

/**
 * Gebruiksduur van een product: één getal in weken, met een losse status voor hoe betrouwbaar het is.
 * Puur; getest in core/rules/usage.test.ts. Slim bijvullen (refillPlan.ts) rekent met wat hier uitkomt.
 *
 * Volgorde: een geldige `lastsWeeks` wint; anders de oude tekst `usesPerBottle` ("± 3 maanden"),
 * die altijd als voorlopige schatting telt. Een ongeldige waarde (0, negatief, geen heel getal,
 * boven de 104) telt als ontbrekend, nooit als "elke week".
 */

export const LASTS_WEEKS_MIN = 1
export const LASTS_WEEKS_MAX = 104

export const lastsWeeksSources: readonly { value: LastsWeeksSource; label: string; hint: string }[] = [
  { value: 'schatting', label: 'Voorlopige schatting', hint: 'Realistische eerste schatting, nog niet bevestigd met praktijkgegevens.' },
  { value: 'leverancier', label: 'Leveranciersindicatie', hint: 'Gebaseerd op informatie of advies van het merk.' },
  { value: 'praktijk', label: 'Praktijkgegevens', hint: 'Bevestigd met gebruikservaringen of betrouwbare verbruiksdata.' },
]

export const PROVISIONAL_HINT = 'Deze gebruiksduur is voorlopig en moet nog met praktijkgegevens worden gecontroleerd.'

export const isValidLastsWeeks = (n: unknown): n is number =>
  typeof n === 'number' && Number.isInteger(n) && n >= LASTS_WEEKS_MIN && n <= LASTS_WEEKS_MAX

type UsageFields = Pick<Product, 'lastsWeeks' | 'lastsWeeksSource' | 'usesPerBottle'>

/** Hoe lang één verpakking meegaat, in weken. `null` = onbekend (of ongeldig). */
export function productLastsWeeks(p: UsageFields): number | null {
  if (isValidLastsWeeks(p.lastsWeeks)) return p.lastsWeeks
  const legacy = lastsWeeksOf(p.usesPerBottle)
  return isValidLastsWeeks(legacy) ? legacy : null
}

export type UsageStatus =
  | { weeks: null; source: null; provisional: false; legacy: false }
  | { weeks: number; source: LastsWeeksSource; provisional: boolean; legacy: boolean }

/** De waarde plus hoe betrouwbaar hij is. `provisional` = nog een schatting (uitroepteken in het beheer). */
export function usageStatus(p: UsageFields): UsageStatus {
  const weeks = productLastsWeeks(p)
  if (weeks === null) return { weeks: null, source: null, provisional: false, legacy: false }
  const legacy = !isValidLastsWeeks(p.lastsWeeks)
  const source: LastsWeeksSource = legacy ? 'schatting' : (p.lastsWeeksSource ?? 'schatting')
  return { weeks, source, provisional: source === 'schatting', legacy }
}

/** Voor de klant: altijd met "±", nooit als exacte belofte. 13 weken en meer in maanden. */
export function weeksLabel(weeks: number): string {
  if (weeks < 13) return `± ${weeks} ${weeks === 1 ? 'week' : 'weken'}`
  const months = Math.round(weeks / 4.35)
  return `± ${months} maanden`
}

/** Wat de winkel toont bij "gaat … mee": de numerieke waarde, anders de oude tekst. Leeg = niets tonen. */
export function usageLabel(p: Pick<Product, 'lastsWeeks' | 'usesPerBottle'>): string {
  return isValidLastsWeeks(p.lastsWeeks) ? weeksLabel(p.lastsWeeks) : (p.usesPerBottle ?? '')
}
